import React from 'react';
import { Button, Col, Form, Input, List, Modal, Row, Typography } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import type { CreateVendorFormValues, CreateVendorModalProps, DedupCandidate } from '../types';

/**
 * Modal dialog for creating a new vendor record and immediately mapping it to a supplier.
 *
 * Two-phase flow:
 * 1. **Form phase** — Vendor name (required) and domain (optional). On submit, fires
 * `onFinish` which calls the create-and-map API with `dedupAcknowledged: false`.
 * 2. **Dedup review phase** — If near-duplicate vendors exist, displays the candidate list.
 * User can either select an existing vendor (`onUseDedupMatch`) or confirm creation
 * anyway (`onConfirmDedupCreate`).
 * @param {CreateVendorModalProps} props - Component props.
 * @returns {React.ReactElement} The create-vendor modal.
 */
const CreateVendorModal: React.FC<CreateVendorModalProps> = ({
  open,
  isSubmitting,
  vendorTarget,
  form,
  onCancel,
  onFinish,
  dedupCandidates,
  onConfirmDedupCreate,
  onUseDedupMatch
}) => {
  const showDedupReview = dedupCandidates !== null && dedupCandidates.length > 0;

  return (
    <Modal title="Create New Vendor" open={open} destroyOnClose onCancel={onCancel} footer={null} width={520}>
      <Typography.Paragraph className="mb-4">
        Create a new vendor record to map to <Typography.Text strong>{vendorTarget}</Typography.Text>
      </Typography.Paragraph>

      {/* ── Dedup review phase ──────────────────────────────────────────── */}
      {showDedupReview && (
        <div className="mb-4">
          <div className="p-3 border border-orange-300 bg-orange-50 rounded mb-3">
            <Typography.Text strong>
              <WarningOutlined className="mr-1 text-orange-500" />
              Similar vendors already exist
            </Typography.Text>
            <Typography.Paragraph className="mt-1 mb-0 text-gray-600">
              Review the matches below. You can map to an existing vendor or create a new one anyway.
            </Typography.Paragraph>
          </div>

          <div className="max-h-72 overflow-y-auto">
            <List
              size="small"
              bordered
              dataSource={dedupCandidates!}
              renderItem={(candidate: DedupCandidate) => (
                <List.Item
                  actions={[
                    <Button
                      key="use"
                      type="primary"
                      size="small"
                      onClick={() => onUseDedupMatch(candidate)}
                      loading={isSubmitting}
                    >
                      Use This
                    </Button>
                  ]}
                >
                  <List.Item.Meta title={candidate.name} description={candidate.domain || undefined} />
                </List.Item>
              )}
            />
          </div>

          <Row justify="end" gutter={8} className="mt-4">
            <Col>
              <Button onClick={onCancel}>Cancel</Button>
            </Col>
            <Col>
              <Button type="primary" danger onClick={onConfirmDedupCreate} loading={isSubmitting}>
                Create Anyway
              </Button>
            </Col>
          </Row>
        </div>
      )}

      {/* ── Form phase ─────────────────────────────────────────────────── */}
      {!showDedupReview && (
        <Form<CreateVendorFormValues> form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            label="Vendor Name"
            name="vendorName"
            rules={[{ required: true, message: 'Vendor name is required.' }]}
          >
            <Input placeholder="Enter vendor name" />
          </Form.Item>

          <Form.Item label="Vendor Domain" name="vendorDomain">
            <Input placeholder="example.com (optional)" />
          </Form.Item>

          <Row justify="end" gutter={8}>
            <Col>
              <Button onClick={onCancel}>Cancel</Button>
            </Col>
            <Col>
              <Button type="primary" htmlType="submit" loading={isSubmitting}>
                Create &amp; Map Vendor
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </Modal>
  );
};

CreateVendorModal.displayName = 'CreateVendorModal';

export default React.memo(CreateVendorModal);
